<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { CmsDepth } from '@pkg/config/misc/cmsDepth';
import { BagHeader } from '@pkg/ui/components/ui/bag-header';
import type { SurpriseBag } from '@pkg/types/surpriseBag';
import { surpriseBagUrlHelper } from '../../../../../apps/web/app/helpers/surpriseBagHelper';
import { useBuildTimeSanityContent } from '../../../../../apps/web/app/composables/useBuildTimeSanityContent';
import { useSearchParams } from '../../../../../apps/web/app/composables/useSearchParams';
import SurpriseBagPrice from '../../../../../apps/web/app/components/blocks/SurpriseBag/SurpriseBagPrice.vue';
import { useI18n } from 'vue-i18n';
import { getPrice } from '@pkg/utilities/price';
import {
  isBagAvailable,
  getCollectionWindowLabel,
  getPickupTag,
  getUnavailableLabel,
  getTitle,
  getItemCategoryLabel,
  getItemTagBadges,
} from '@pkg/utilities/surprisebag';
import SurpriseBagCollectionTime from '../../../../../apps/web/app/components/blocks/SurpriseBag/SurpriseBagCollectionTime.vue';

const props = defineProps<{
  article?: SurpriseBag;
}>();

const emit = defineEmits<{
  'item-clicked': [bag: SurpriseBag];
}>();

const { locale, t } = useI18n();
const { buildPathWithSearchParams } = useSearchParams();

// Loaded after mount so the card stays synchronous. Awaiting the labels file here
// suspends hydration, and the bag page can then apply live availability while these
// cards are still hydrating.
const staticTranslations = ref<Record<string, any>>({});

onMounted(async() => {
  staticTranslations.value = await useBuildTimeSanityContent(locale.value);
});

const bagHeaderProps = computed(() => ({
  image: props.article?.item?.coverPicture?.currentUrl,
  logo: props.article?.item?.logoPicture?.currentUrl,
  ratings: props.article?.item?.averageOverallRating?.averageOverallRating,
  altText: `${props.article?.displayName} - ${props.article?.store?.storeLocation?.address?.country?.name} - Too Good To Go`,
  availableBag: isAvailable.value,
  logoAltText: props.article?.item?.logoPicture?.altText,
  tags: getItemTagBadges(props.article?.itemTags),
  tagsClickable: false,
}));

const isAvailable = computed(() => {
  return isBagAvailable(
    props.article?.itemsAvailable,
    props.article?.inSalesWindow,
    !!props.article?.pickupInterval,
  );
});

const currentPriceProps = computed(() => ({
  availability: isAvailable.value,
  oldPrice: getPrice(
    props?.article?.item?.itemValue,
    locale.value),
  currentPrice: getPrice(
    props?.article?.item?.itemPrice,
    locale.value),
  alignRight: true,
}));

const cardContentProps = computed(() => {
  const title = props.article?.displayName || '';
  const subtitle = staticTranslations.value?.surpriseBagKeyword || '';
  const distance = ` | ${(props.article?.distance ?? 0).toFixed(1)} km`;
  const timeTagText = getPickupTag(
    props.article?.pickupInterval?.start,
    props.article?.store?.storeLocation?.address?.country?.isoCode);
  const dayTranslation = t(timeTagText) || timeTagText;
  const otherDayText = staticTranslations.value?.timingTexts?.otherDayText || '';
  const dayLabel = `${otherDayText} ${dayTranslation.toLowerCase()}`.trim();
  
  const pickupWindow = getCollectionWindowLabel(
    props.article?.pickupInterval?.start,
    props.article?.pickupInterval?.end,
    dayLabel,
  );
  
  const collection = {
    timeObject: typeof pickupWindow === 'string' ? undefined : pickupWindow,
    timeTag: timeTagText,
    availableBag: isAvailable.value,
    soldOutText: getUnavailableLabel(props.article?.itemTags),
  };

  return {
    title,
    subtitle,
    collection,
    distance,
  };
});

const link = computed(() => {
  if (!props.article?.item?.itemId) {
    return undefined;
  }

  const localeForSlug =
    props.article?.localizationCode?.toLowerCase() === 'ja-jp' ? 'ja' : locale.value;
  const basePath = surpriseBagUrlHelper(
    props.article?.item?.itemId,
    localeForSlug,
    props.article?.store?.storeLocation?.address?.city,
    props.article?.displayName,
    getItemCategoryLabel(props.article?.item),
    props.article?.title,
    true,
  );

  const urlLocale = (locale.value ?? '').toLowerCase();
  const slugLocale = (localeForSlug ?? '').toLowerCase();
  const basePathWithLocale =
    slugLocale && urlLocale && basePath.startsWith(`/${slugLocale}/`)
      ? `/${urlLocale}/${basePath.slice(slugLocale.length + 2)}`
      : basePath;

  return buildPathWithSearchParams(basePathWithLocale);
});

const displayTitle = computed(() => {
  return (
    getTitle(
      props.article?.store?.storeName,
      props.article?.store?.branch,
    ) || props.article?.displayName || ''
  );
});

</script>

<template>
  <article :id="props.article?.item?.itemId">
    <NuxtLink :to="link || '#'" class="card" @click="emit('item-clicked', article!)">
    <BagHeader v-bind="bagHeaderProps"
      class="image"
    />
    <section class="content text">
      <h5 class="content__title"><span>{{ displayTitle }}</span></h5>
      <p class="content__description">
        <span class="content__description__text">{{ cardContentProps?.subtitle }}</span>
        <span class="content__description__text">
          <SurpriseBagCollectionTime
            :renderCollectionTimeInline="true"
            v-bind="cardContentProps?.collection"
          />
          <span>{{ cardContentProps?.distance }}</span>
        </span>
      </p>
      <footer class="content__footer" :data-cms-depth="CmsDepth.FooterSurprise">
        <SurpriseBagPrice v-bind="currentPriceProps" />
      </footer>
    </section>
    </NuxtLink>
  </article>
</template>

<style scoped>
@import '../../../assets/css/mixins/_mixins.index.pcss';

.card {
  border-radius: 5px;
  background-color: white;
  border: 1px solid rgba(37, 45, 45, 0.075);
  border-radius: 0.5rem;
  height: 275px;
  box-shadow: 0px 2px 8px 2px rgba(37, 45, 45, 0.075);
  padding: 4px;
  @mixin body-small;
  color: var(--theme-text-color-app);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  width: 330px;
  margin: 0 auto;

  @screen tablet {
    width: 340px;
  }

  &:hover {
    border: 1px solid theme('colors.primary.30');
  }
}

.card.card:hover {
  color: var(--theme-text-color-app);
}

.content {
  height: 30%;
  text-align: left;

  &__title {
    padding: 4px 4px 0px 4px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    
    .card & {
      @mixin body-large;
      font-weight: bold;
    }
  }

  &__description {
    display: flex;
    flex-direction: column;
    padding: 0px 4px;
    
    &__text {
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      min-width: 0;
    }
  }

  &__footer {
    margin-top: 4px;
    border-top: 1px theme('colors.neutral.30') dashed ;
    display: flex;
    justify-content: flex-end;
    @mixin body-large;
    font-weight: bold;

    &__text {
      padding: 0px 4px 4px 4px;
      color: var(--theme-text-color-app);
      
      &__fade {
        opacity: 0.5;
      }

      &-old {
        @mixin body-small;
        text-decoration: line-through;
        color: theme('colors.neutral.70');
        margin-right: 8px;
      }
    }
  }
}
.image {
  width: 100%;
  height: 60%;
  overflow: hidden;
  border-radius: 5px;
}
</style>